import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getStoredAuth } from '@/shared/auth/auth-storage';
import { APP_FEATURE_GUARDS } from '@/shared/constants/app-permissions';
import { useHasAnyAuthority } from '@/shared/auth/use-has-any-authority';
import { getChatRoomDisplayName } from '@/shared/model/chat-room.model';
import { useCrmChatContext } from '@/shared/providers/CrmChatProvider';
import { useAppSelector } from '@/store';
import { ChatConversationPanel } from './widget/ChatConversationPanel';
import { ChatRoomsPanel } from './widget/ChatRoomsPanel';
import { ChatToggleButton } from './widget/ChatToggleButton';
import { CRM_CHAT_ENABLED } from './crm-chat-setup';
function CrmChatWidgetShell() {
    const navigate = useNavigate();
    const canSendMessages = useHasAnyAuthority(APP_FEATURE_GUARDS.chatMessageCreate);
    const chat = useCrmChatContext();
    const { rooms: chatRooms, directoryUsers } = useAppSelector((s) => s.chatRoom);
    const username = chat.chatUsername;
    const { widgetOpen, setWidgetOpen, totalUnread } = chat;
    const connected = chat.connectionState === 'connected';
    const roomList = useMemo(() => chatRooms
        .filter((r) => r.id != null && !r.archived)
        .map((r) => ({
        id: r.id,
        label: getChatRoomDisplayName(r, username, directoryUsers),
    })), [chatRooms, directoryUsers, username]);
    const activeRoom = useMemo(() => roomList.find((r) => r.id === chat.currentRoomId) ?? null, [chat.currentRoomId, roomList]);
    const headerTitle = activeRoom?.label ?? (chat.currentRoomId != null ? 'Chat' : 'Welcome');
    const entries = chat.currentRoomId != null ? (chat.entriesByRoom[chat.currentRoomId] ?? []) : [];
    const { connectionState, currentRoomId, selectRoom } = chat;
    useEffect(() => {
        if (!widgetOpen || connectionState !== 'connected' || currentRoomId != null)
            return;
        const first = roomList[0];
        if (first)
            selectRoom(first.id);
    }, [widgetOpen, connectionState, currentRoomId, roomList, selectRoom]);
    const openFullPageChat = useCallback(() => {
        setWidgetOpen(false);
        navigate('/crm/chat');
    }, [navigate, setWidgetOpen]);
    return (_jsxs(_Fragment, { children: [_jsx(ChatToggleButton, { open: widgetOpen, unreadCount: totalUnread, onToggle: () => setWidgetOpen(!widgetOpen) }), _jsxs("div", { id: "crm-chat-widget-panel", className: `crm-chat-widget__panel ${widgetOpen ? 'crm-chat-widget__panel--open' : ''}`, hidden: !widgetOpen, children: [_jsx(ChatRoomsPanel, { rooms: roomList, selectedRoomId: chat.currentRoomId, unreadByRoom: chat.unreadByRoom, connected: connected, onSelectRoom: chat.selectRoom }), _jsx(ChatConversationPanel, { title: headerTitle, roomSelected: chat.currentRoomId != null, connected: connected, currentUsername: username, entries: entries, typingUsers: chat.typingUsers, canSend: connected && chat.currentRoomId != null && canSendMessages, onSend: (text) => void chat.sendMessage(text), onTyping: chat.notifyTyping, onOpenFullPage: openFullPageChat })] })] }));
}
/** Floating CRM chat (bottom-right). Hidden on login and on the full-page chat route. */
export default function CrmChatWidget() {
    const { pathname } = useLocation();
    const canAccessChat = useHasAnyAuthority(APP_FEATURE_GUARDS.chatAccess);
    if (!CRM_CHAT_ENABLED || !getStoredAuth() || !canAccessChat)
        return null;
    if (pathname === '/crm/chat')
        return null;
    return (_jsx("div", { className: "crm-chat-widget", children: _jsx(CrmChatWidgetShell, {}) }));
}
